import React, { useState, useEffect } from 'react';
import { RiskCard, RiskCardHeader, RiskCardBody, RiskButton } from '../../components/risk/UiKit';

const API_BASE = process.env.REACT_APP_API_URL + '/api';

export default function ApprovalSettings() {
  const [settings, setSettings] = useState({
    require_ceo_approval: true,
    ceo_approval_method: 'evidence',
    ceo_email: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API_BASE}/risk-assessment/organization/settings`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success) {
        setSettings(data.settings);
      } else {
        setMessage('❌ Failed to load settings');
      }
    } catch (err) {
      setMessage('❌ Error loading settings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');

    try {
      const res = await fetch(`${API_BASE}/risk-assessment/organization/settings`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });

      const data = await res.json();
      if (data.success) {
        setMessage('✅ Settings saved successfully');
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage(`❌ ${data.message}`);
      }
    } catch (err) {
      setMessage('❌ Error saving settings');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Approval Settings</h1>
        <p className="text-gray-600 mt-2">Configure how risk assessments are approved in your organization</p>
      </div>

      {message && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.includes('✅') ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          {message}
        </div>
      )}

      <RiskCard>
        <RiskCardHeader 
          title="CEO/Senior Executive Approval" 
          subtitle="Configure whether senior executive approval is required for risk assessments"
        />
        <RiskCardBody>
          <div className="space-y-6">
            {/* CEO Approval Required */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Require CEO/Senior Executive Approval</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Enable this to require senior executive approval before assessments can be published
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="require_ceo_approval"
                  checked={settings.require_ceo_approval}
                  onChange={handleInputChange}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {/* Approval Method */}
            {settings.require_ceo_approval && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-3">
                    Approval Method
                  </label>
                  <div className="space-y-3">
                    <label className="flex items-center p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="ceo_approval_method"
                        value="system"
                        checked={settings.ceo_approval_method === 'system'}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">System Login</div>
                        <div className="text-sm text-gray-600">CEO logs into the system to approve assessments</div>
                      </div>
                    </label>
                    
                    <label className="flex items-center p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="ceo_approval_method"
                        value="evidence"
                        checked={settings.ceo_approval_method === 'evidence'}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">Evidence Documentation</div>
                        <div className="text-sm text-gray-600">Upload evidence of CEO approval (emails, meeting notes, etc.)</div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* CEO Email */}
                <div>
                  <label htmlFor="ceo_email" className="block text-sm font-medium text-gray-900 mb-2">
                    CEO/Senior Executive Email
                  </label>
                  <input
                    type="email"
                    id="ceo_email"
                    name="ceo_email"
                    value={settings.ceo_email}
                    onChange={handleInputChange}
                    placeholder="ceo@company.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Optional: Used for notifications and reference</p>
                </div>
              </div>
            )}

            {/* Save Button */}
            <div className="flex justify-end pt-6 border-t border-gray-200">
              <RiskButton
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2"
              >
                {saving ? (
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Saving...
                  </div>
                ) : (
                  'Save Settings'
                )}
              </RiskButton>
            </div>
          </div>
        </RiskCardBody>
      </RiskCard>

      {/* Information Card */}
      <RiskCard className="mt-6">
        <RiskCardHeader title="How It Works" />
        <RiskCardBody>
          <div className="space-y-4 text-sm text-gray-600">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Assessment Approval Workflow:</h4>
              <ol className="list-decimal list-inside space-y-1 ml-4">
                <li>Assessment is compiled by Risk Officer</li>
                <li>Risk Officer approves the assessment</li>
                {settings.require_ceo_approval ? (
                  <>
                    <li>Senior Executive approval is required</li>
                    {settings.ceo_approval_method === 'system' ? (
                      <li>CEO logs into system to approve</li>
                    ) : (
                      <li>Evidence of CEO approval is uploaded</li>
                    )}
                    <li>Assessment is published after CEO approval</li>
                  </>
                ) : (
                  <li>Assessment is published after Officer approval</li>
                )}
              </ol>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Evidence Documentation:</h4>
              <p>When using evidence documentation, you can upload:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Emails from the CEO approving the assessment</li>
                <li>Meeting notes documenting verbal approval</li>
                <li>Signed documents or forms</li>
                <li>Other evidence of senior executive approval</li>
              </ul>
            </div>
          </div>
        </RiskCardBody>
      </RiskCard>
    </div>
  );
}
